import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { Card } from "antd";

interface AssignmentData {
  on_time_completed: number;
  delayed_completed: number;
  delayed_incomplete: number;
  active: number;
}

interface TimePeriodData {
  total_assignments: number;
  assignments: AssignmentData;
  period_start: string;
  period_end: string;
}

interface VisitorData {
  visitor_id: number;
  visitor_name: string;
  time_periods: {
    current_week: TimePeriodData;
    last_week: TimePeriodData;
    two_weeks_ago: TimePeriodData;
  };
  total_assignments: number;
  total_completed: number;
}

interface VisitorPerformanceData {
  time_periods: {
    current_week: { start_date: string; end_date: string; label: string };
    last_week: { start_date: string; end_date: string; label: string };
    two_weeks_ago: { start_date: string; end_date: string; label: string };
  };
  visitors: VisitorData[];
  assignment_categories: {
    [key: string]: { label: string; description: string };
  };
}
interface Props {
  visitor_performance: VisitorPerformanceData;
}

const VisitorPerformanceChart: React.FC<Props> = ({ visitor_performance }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const categories = [
    {
      key: "on_time_completed",
      color: "#4CAF50",
      labelFr: "Complétées à temps",
    },
    {
      key: "delayed_completed",
      color: "#FF9800",
      labelFr: "Complétées en retard",
    },
    { key: "delayed_incomplete", color: "#F44336", labelFr: "En retard" },
    { key: "active", color: "#2196F3", labelFr: "Actives" },
  ];

  const weeks = [
    {
      key: "two_weeks_ago",
      label: visitor_performance?.time_periods?.two_weeks_ago?.label,
      labelFr: "Il y a deux semaines",
    },
    {
      key: "last_week",
      label: visitor_performance?.time_periods?.last_week?.label,
      labelFr: "Semaine dernière",
    },
    {
      key: "current_week",
      label: visitor_performance?.time_periods?.current_week?.label,
      labelFr: "Semaine courante",
    },
  ];

  useEffect(() => {
    if (!visitor_performance?.visitors || !chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const xAxisData = visitor_performance.visitors.map(
      (visitor) => visitor.visitor_name
    );

    const series: echarts.BarSeriesOption[] = weeks.flatMap((week) =>
      categories.map((category) => ({
        name: `${category.labelFr} (${week.labelFr})`,
        type: "bar",
        stack: week.key, // Stack bars per week, not per visitor
        emphasis: { focus: "series" },
        itemStyle: { color: category.color },
        data: visitor_performance.visitors.map(
          (visitor: any) =>
            visitor.time_periods?.[week.key]?.assignments?.[
              category.key as keyof AssignmentData
            ] || 0
        ),
      }))
    );

    // Création des noms de séries pour la légende
    const legendData: string[] = [];
    series.forEach((s) => {
      if (s.name && typeof s.name === "string") {
        legendData.push(s.name);
      }
    });

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params: any) => {
          const visitor = visitor_performance.visitors[params[0].dataIndex];
          let tooltip = `<strong>${visitor.visitor_name}</strong><br/>`;

          // Ajout des données d'affectations hebdomadaires en français
          weeks.forEach((week) => {
            const weekData =
              visitor.time_periods[
                week.key as keyof typeof visitor.time_periods
              ];
            if (weekData) {
              tooltip += `<br/><b>${week.labelFr}:</b> ${weekData.total_assignments} affectations<br/>`;

              // Afficher la répartition pour chaque catégorie
              categories.forEach((category) => {
                const count =
                  weekData.assignments[category.key as keyof AssignmentData];
                tooltip += `- ${category.labelFr}: ${count}<br/>`;
              });
            }
          });

          tooltip += `<hr/><small>Affectations totales: ${visitor.total_assignments}<br/>`;
          tooltip += `Affectations complétées: ${visitor.total_completed}</small>`;

          return tooltip;
        },
      },

      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: xAxisData,
        axisLabel: { rotate: 45 },
      },
      yAxis: { type: "value", name: "Affectations" },
      series: series,
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [visitor_performance]);

  // Génération de la légende des couleurs simplifiée
  const renderSimpleColorLegend = () => {
    return (
      <div style={{ marginTop: "15px", marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "15px",
          }}
        >
          {categories.map((category) => (
            <div
              key={category.key}
              style={{ display: "flex", alignItems: "center" }}
            >
              <div
                style={{
                  width: "15px",
                  height: "15px",
                  backgroundColor: category.color,
                  marginRight: "8px",
                  borderRadius: "3px",
                }}
              ></div>
              <span>{category.labelFr}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!visitor_performance?.visitors?.length)
    return <div>Aucune donnée de visiteur disponible</div>;

  return (
    <Card style={{ border: "2px solid #42005a", marginTop: "20px" }}>
      <div>
        <h3 style={{ textAlign: "center" }}>Performance des visiteurs</h3>
        <p>
          Statut des affectations des visiteurs dans les trois dernières
          semaines
        </p>

        <div ref={chartRef} style={{ width: "100%", height: "400px" }} />
        {renderSimpleColorLegend()}
        {visitor_performance.time_periods && (
          <div>
            <h4>Semaines</h4>
            <ul>
              <li>
                de {visitor_performance.time_periods.current_week.start_date} à{" "}
                {visitor_performance.time_periods.current_week.end_date}
              </li>
              <li>
                de {visitor_performance.time_periods.last_week.start_date} à{" "}
                {visitor_performance.time_periods.last_week.end_date}
              </li>
              <li>
                de {visitor_performance.time_periods.two_weeks_ago.start_date} à{" "}
                {visitor_performance.time_periods.two_weeks_ago.end_date}
              </li>
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
};

export default VisitorPerformanceChart;
